import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ProgressBar } from '../ProgressBar'

export interface UploadQueueItem {
  id: string
  src: string
  /** 0-100. Reaching 100 reveals the photo fully and shows the checkmark. */
  progress: number
}

export interface UploadQueueProps extends Omit<BoxProps, 'children'> {
  items: UploadQueueItem[]
  /** e.g. "Uploading 4 of 6…" or "All photos in" */
  statusLabel: React.ReactNode
  /** e.g. "62%" */
  percentLabel: React.ReactNode
  /** Overall progress 0-100 for the bottom bar */
  progress: number
  /**
   * Called with an item's id when its remove button is clicked. Omit this
   * prop entirely (e.g. once the real upload has started) to hide the
   * remove affordance on every tile.
   */
  onRemove?: (id: string) => void
}

const Top = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: 10,
})

const StatusText = styled('span')({
  fontSize: 12.5,
  fontWeight: 650,
  color: 'var(--ds-ink)',
})

const PercentText = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  color: 'var(--ds-ink-subtle)',
})

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  gap: 7,
})

const Tile = styled(Box)({
  position: 'relative',
  aspectRatio: '1',
  borderRadius: 8,
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  '&:hover .upload-queue-remove, &:focus-within .upload-queue-remove': {
    transform: 'scale(1.1)',
  },
})

// Always visible — hover-to-reveal isn't discoverable before a first upload
// (nobody hovers a tile to find out it's removable), and hover doesn't exist
// on touch at all. Hover/focus now only adds a bit of emphasis, not reveal.
const RemoveButton = styled('button')({
  all: 'unset',
  position: 'absolute',
  top: 3,
  right: 3,
  width: 18,
  height: 18,
  borderRadius: '50%',
  background: 'rgba(10,10,14,0.72)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 10,
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  transition: 'transform var(--ds-duration-fast, 120ms) var(--ds-ease), background-color var(--ds-duration-fast, 120ms) var(--ds-ease)',
  '&:hover': {
    background: 'var(--ds-danger, #e5484d)',
    transform: 'scale(1.1)',
  },
  '&:focus-visible': {
    outline: '2px solid #fff',
    outlineOffset: 1,
  },
})

const Veil = styled(Box, {
  shouldForwardProp: (p) => p !== 'progress',
})<{ progress: number }>(({ progress }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: `${Math.max(0, 100 - Math.min(100, progress))}%`,
  background: 'rgba(255,255,255,0.8)',
  transition: 'height var(--ds-duration-slow, 300ms) var(--ds-ease)',
}))

const Checkmark = styled(Box)({
  position: 'absolute',
  right: 4,
  bottom: 4,
  width: 15,
  height: 15,
  borderRadius: '50%',
  background: 'var(--ds-ink)',
  color: 'var(--ds-inverse-ink)',
  fontSize: 8,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

/** Grid of uploading thumbnails with a receding veil, checkmark on completion, and an overall bar. */
export function UploadQueue({ items, statusLabel, percentLabel, progress, onRemove, ...rest }: UploadQueueProps) {
  return (
    <Box {...rest}>
      <Top>
        <StatusText>{statusLabel}</StatusText>
        <PercentText>{percentLabel}</PercentText>
      </Top>
      <Grid>
        {items.map((item) => {
          // Once an item has actually started uploading its own removal would
          // desync it from the in-flight index-based progress callback — only
          // offer the remove affordance while it's still sitting at 0.
          const canRemove = Boolean(onRemove) && item.progress === 0
          return (
            <Tile key={item.id}>
              <img src={item.src} alt='' loading='lazy' />
              <Veil progress={item.progress} />
              {item.progress >= 100 && <Checkmark aria-hidden>✓</Checkmark>}
              {canRemove && (
                <RemoveButton
                  type='button'
                  className='upload-queue-remove'
                  aria-label='Remove photo'
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove?.(item.id)
                  }}
                >
                  ✕
                </RemoveButton>
              )}
            </Tile>
          )
        })}
      </Grid>
      <Box sx={{ mt: '10px' }}>
        <ProgressBar value={progress} />
      </Box>
    </Box>
  )
}
